import type { ComponentType } from "react";
import type { QueryClient } from "@tanstack/react-query";
import type { SessionMethodReturn, SupportedSession, TellescopeModelFromName } from "@tellescope/tanstack-query";
import { fetchInfiniteTellescopeQuery, fetchTellescopeQuery } from "@tellescope/tanstack-query";
import { toast } from "sonner";
import {
  IMPLEMENTED_NOTIFICATION_CONFIGS,
  IMPLEMENTED_NOTIFICATION_IDS,
  ImplementedNotificationId,
} from "../constants";
import { SMSNotification } from "./SMSNotification";
import { EmailNotification } from "./EmailNotification";
import { ChatNotification } from "./ChatNotification";
import { TicketNotification } from "./TicketNotification";
import { EnduserNotification } from "./EnduserNotification";
import { discriminatedStringUnionIncludes } from "@tellescope/utils";
import { Button } from "@tellescope/ui";
import { X } from "lucide-react";

/**
 * Registry of custom toast handlers for notification types that have rich UI components.
 *
 * To add a new rich notification toast:
 * 1. Create a toast component (e.g. `TicketNotification.tsx`)
 * 2. Add an entry here with `defineHandler` mapping the notification type ID to its handler config
 *
 * Notification types present in NOTIFICATIONS_CONFIG but absent from this registry
 * will automatically receive a generic toast displaying the notification message.
 *
 * `prefetchData` is called before the toast is rendered to pre-load related data.
 * Return `null` from `prefetchData` to suppress the notification entirely.
 */
type NotificationHandler<TPrefetchedData extends Record<string, unknown> = Record<string, unknown>> = {
  /** The `relatedRecords[].type` value to look up the related record ID */
  relatedRecordType: string;
  /** The React component to render as a custom toast */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  component: ComponentType<any>;
  /** Pre-fetches related data before the toast is rendered. Return `null` to suppress the notification. */
  prefetchData: (
    relatedId: string,
    notification: TellescopeModelFromName<"user_notifications">,
    queryClient: QueryClient,
    session: SupportedSession
  ) => Promise<TPrefetchedData | null>;
  /** Maps the pre-fetched data and notification info to component props */
  mapProps: (
    relatedId: string,
    notification: TellescopeModelFromName<"user_notifications">,
    toastId: string | number,
    prefetchedData: TPrefetchedData
  ) => Record<string, unknown>;
  /** Extra logic that determines if the notification should be displayed */
  predicate?: ({
    notification,
    prefetchedData,
    currentUser,
  }: {
    notification: TellescopeModelFromName<"user_notifications">;
    prefetchedData: TPrefetchedData;
    currentUser: TellescopeModelFromName<"users">;
  }) => boolean;
};

const defineHandler = <T extends Record<string, unknown>>(handler: NotificationHandler<T>): NotificationHandler =>
  handler as NotificationHandler;

type SMSPrefetchedData = {
  sms: TellescopeModelFromName<"sms_messages">;
  enduser: TellescopeModelFromName<"endusers"> | undefined;
};

type EmailPrefetchedData = {
  email: TellescopeModelFromName<"emails">;
  enduser: TellescopeModelFromName<"endusers"> | undefined;
};

type ChatPrefetchedData = {
  chatroom: TellescopeModelFromName<"chat_rooms">;
  message: TellescopeModelFromName<"chats">;
  endusers: TellescopeModelFromName<"endusers">[];
};

type EnduserPrefetchedData = {
  enduser: TellescopeModelFromName<"endusers">;
};

type TicketPrefetchedData = {
  ticket: TellescopeModelFromName<"tickets">;
};

type CareTeamTicketPrefetchedData = {
  ticket: TellescopeModelFromName<"tickets">;
  enduser: TellescopeModelFromName<"endusers">;
};

const NOTIFICATION_HANDLERS: Partial<Record<ImplementedNotificationId, NotificationHandler>> = {
  newSMS: defineHandler<SMSPrefetchedData>({
    relatedRecordType: "sms",
    component: SMSNotification,
    prefetchData: async (relatedId, _notification, queryClient, session) => {
      const sms = await fetchTellescopeQuery({
        queryClient,
        session,
        model: "sms_messages",
        method: "getOne",
        request: { id: relatedId },
      });
      const enduser = sms.enduserId
        ? await fetchTellescopeQuery({
            queryClient,
            session,
            model: "endusers",
            method: "getOne",
            request: { id: sms.enduserId },
          })
        : undefined;
      return { sms, enduser };
    },
    mapProps: (_relatedId, notification, toastId, { sms, enduser }) => ({
      message: notification.message,
      sms,
      enduser,
      toastId,
    }),
    predicate: ({ currentUser, prefetchedData }) => {
      if (currentUser.id === prefetchedData.sms.creator || !prefetchedData.sms.inbound) return false;
      return true;
    },
  }),
  newEmail: defineHandler<EmailPrefetchedData>({
    relatedRecordType: "email",
    component: EmailNotification,
    prefetchData: async (relatedId, _notification, queryClient, session) => {
      const email = await fetchTellescopeQuery({
        queryClient,
        session,
        model: "emails",
        method: "getOne",
        request: { id: relatedId },
      });
      const enduser = email.enduserId
        ? await fetchTellescopeQuery({
            queryClient,
            session,
            model: "endusers",
            method: "getOne",
            request: { id: email.enduserId },
          })
        : undefined;
      return { email, enduser };
    },
    mapProps: (_relatedId, notification, toastId, { email, enduser }) => ({
      message: notification.message,
      email,
      enduser,
      toastId,
    }),
    predicate: ({ currentUser, prefetchedData }) => {
      if (currentUser.id === prefetchedData.email.creator || !prefetchedData.email.inbound) return false;
      return true;
    },
  }),
  newChatMessage: defineHandler<ChatPrefetchedData>({
    relatedRecordType: "chatRoom",
    component: ChatNotification,
    prefetchData: async (relatedId, _notification, queryClient, session) => {
      const chatroom = await fetchTellescopeQuery({
        queryClient,
        session,
        model: "chat_rooms",
        method: "getOne",
        request: relatedId,
      });
      const chatRes = await fetchInfiniteTellescopeQuery({
        queryClient,
        session,
        model: "chats",
        method: "getSome",
        request: {
          filter: {
            roomId: relatedId,
          },
          limit: 1,
        },
        initialPageParam: undefined,
        getNextPageParam: (page) => page.at(-1)?.id,
        paginateRequest: (lastId, request) => ({
          ...request,
          lastId,
        }),
      });
      const message = chatRes.pages.at(0)?.at(0);
      if (!message) return null;
      const displayEnduserIds = (chatroom.enduserIds ?? []).slice(0, 3);
      const endusers = displayEnduserIds.length
        ? (
            await fetchTellescopeQuery({
              queryClient,
              session,
              model: "endusers",
              method: "getByIds",
              request: { ids: displayEnduserIds },
            })
          ).matches
        : [];
      return { chatroom, message, endusers };
    },
    mapProps: (_relatedId, notification, toastId, { chatroom, endusers }) => ({
      message: notification.message,
      chatroom,
      endusers,
      toastId,
    }),
    predicate: ({ currentUser, prefetchedData }) => {
      if (
        currentUser.id === prefetchedData.message.creator ||
        !prefetchedData.chatroom.userIds?.includes(currentUser.id)
      )
        return false;
      return true;
    },
  }),
  newTeamChatMessage: defineHandler<ChatPrefetchedData>({
    relatedRecordType: "chatRoom",
    component: ChatNotification,
    prefetchData: async (relatedId, _notification, queryClient, session) => {
      const chatroom = await fetchTellescopeQuery({
        queryClient,
        session,
        model: "chat_rooms",
        method: "getOne",
        request: relatedId,
      });

      const chatRes = await fetchInfiniteTellescopeQuery({
        queryClient,
        session,
        model: "chats",
        method: "getSome",
        request: {
          filter: {
            roomId: relatedId,
          },
          limit: 1,
        },
        initialPageParam: undefined,
        getNextPageParam: (page) => page.at(-1)?.id,
        paginateRequest: (lastId, request) => ({
          ...request,
          lastId,
        }),
      });
      const message = chatRes.pages.at(0)?.at(0);
      if (!message) return null;
      const displayEnduserIds = (chatroom.enduserIds ?? []).slice(0, 3);
      const endusers = displayEnduserIds.length
        ? (
            await fetchTellescopeQuery({
              queryClient,
              session,
              model: "endusers",
              method: "getByIds",
              request: { ids: displayEnduserIds },
            })
          ).matches
        : [];
      return { chatroom, message, endusers };
    },
    mapProps: (_relatedId, notification, toastId, { chatroom, endusers }) => ({
      message: notification.message,
      chatroom,
      endusers,
      toastId,
      teamChat: true,
    }),
    predicate: ({ currentUser, prefetchedData }) => {
      if (
        currentUser.id === prefetchedData.message.creator ||
        !prefetchedData.chatroom.userIds?.includes(currentUser.id)
      )
        return false;
      return true;
    },
  }),
  newTicket: defineHandler<TicketPrefetchedData>({
    relatedRecordType: "ticket",
    component: TicketNotification,
    prefetchData: async (relatedId, _notification, queryClient, session) => {
      const ticket = await fetchTellescopeQuery({
        queryClient,
        session,
        model: "tickets",
        method: "getOne",
        request: relatedId,
      });
      return { ticket };
    },
    mapProps: (relatedId, notification, toastId) => ({
      message: notification.message,
      relatedTicketId: relatedId,
      toastId,
    }),
    predicate: ({ currentUser, prefetchedData }) => {
      if (currentUser.id === prefetchedData.ticket.creator) return false;
      return true;
    },
  }),
  ticketCompletedCareTeam: defineHandler<CareTeamTicketPrefetchedData>({
    relatedRecordType: "ticket",
    component: TicketNotification,
    prefetchData: async (relatedId, _notification, queryClient, session) => {
      const ticket = await fetchTellescopeQuery({
        queryClient,
        session,
        model: "tickets",
        method: "getOne",
        request: relatedId,
      });
      const enduser = ticket.enduserId
        ? await fetchTellescopeQuery({
            queryClient,
            session,
            model: "endusers",
            method: "getOne",
            request: ticket.enduserId,
          })
        : undefined;
      if (!enduser) return null;
      return { ticket, enduser };
    },
    mapProps: (relatedId, notification, toastId) => ({
      message: notification.message,
      relatedTicketId: relatedId,
      toastId,
    }),
    predicate: ({ currentUser, prefetchedData }) => {
      if (!prefetchedData.enduser.assignedTo?.includes(currentUser.id)) return false;
      return true;
    },
  }),
  Zendesk: defineHandler<EnduserPrefetchedData>({
    relatedRecordType: "enduser",
    component: EnduserNotification,
    prefetchData: async (relatedId, _notification, queryClient, session) => {
      const enduser = await fetchTellescopeQuery({
        queryClient,
        session,
        model: "endusers",
        method: "getOne",
        request: relatedId,
      });
      return { enduser };
    },
    mapProps: (relatedId, notification, toastId, { enduser }) => ({
      message: notification.message,
      enduser,
      relatedEnduserId: relatedId,
      toastId,
    }),
    predicate: ({ currentUser, prefetchedData }) => {
      if (prefetchedData.enduser.assignedTo?.length && !prefetchedData.enduser.assignedTo.includes(currentUser.id))
        return false;
      return true;
    },
  }),
  assignedTicket: defineHandler<TicketPrefetchedData>({
    relatedRecordType: "ticket",
    component: TicketNotification,
    prefetchData: async (relatedId, _notification, queryClient, session) => {
      const ticket = await fetchTellescopeQuery({
        queryClient,
        session,
        model: "tickets",
        method: "getOne",
        request: relatedId,
      });

      return { ticket };
    },
    mapProps: (relatedId, notification, toastId) => ({
      message: notification.message,
      relatedTicketId: relatedId,
      toastId,
    }),
    predicate: ({ prefetchedData, currentUser }) => {
      if (
        prefetchedData.ticket.owner === prefetchedData.ticket.creator ||
        prefetchedData.ticket.owner !== currentUser.id
      ) {
        return false;
      }
      return true;
    },
  }),
  newVoicemail: defineHandler<EnduserPrefetchedData>({
    relatedRecordType: "enduser",
    component: EnduserNotification,
    prefetchData: async (relatedId, _notification, queryClient, session) => {
      const enduser = await fetchTellescopeQuery({
        queryClient,
        session,
        model: "endusers",
        method: "getOne",
        request: relatedId,
      });
      return { enduser };
    },
    mapProps: (relatedId, notification, toastId, { enduser }) => ({
      message: notification.message,
      enduser,
      relatedEnduserId: relatedId,
      toastId,
    }),
  }),
  newLabResult: defineHandler<EnduserPrefetchedData>({
    relatedRecordType: "enduser",
    component: EnduserNotification,
    prefetchData: async (relatedId, _notification, queryClient, session) => {
      const enduser = await fetchTellescopeQuery({
        queryClient,
        session,
        model: "endusers",
        method: "getOne",
        request: relatedId,
      });
      return { enduser };
    },
    mapProps: (relatedId, notification, toastId, { enduser }) => ({
      message: notification.message,
      enduser,
      relatedEnduserId: relatedId,
      toastId,
    }),
  }),
};

type DedupedNotification = {
  notification: TellescopeModelFromName<"user_notifications">;
  handler: NotificationHandler | undefined;
  relatedId: string | undefined;
};

export const handleUserNotifications = async (
  userNotifications: SessionMethodReturn<"sync">["results"],
  user: TellescopeModelFromName<"users">,
  queryClient: QueryClient,
  session: SupportedSession
) => {
  if (Notification.permission !== "granted") {
    await Notification.requestPermission();
  }

  const shouldUseOSNotification = !!document.hidden; // TODO: improve this to handle multiple tabs open at the same time

  const dedupedNotifications = userNotifications.reduce<Map<string, DedupedNotification>>((acc, curr) => {
    if (curr.modelName !== "user_notifications") return acc;

    const parsedData = JSON.parse(curr.data) as TellescopeModelFromName<"user_notifications">;

    if (!discriminatedStringUnionIncludes(IMPLEMENTED_NOTIFICATION_IDS, parsedData.type)) return acc;

    const configuredSetting = user.notificationPreferences?.[parsedData.type]?.browser;
    const defaultSetting = IMPLEMENTED_NOTIFICATION_CONFIGS.reduce<boolean | undefined>((acc, curr) => {
      if (acc !== undefined) return acc;
      return curr.settings.find(({ id }) => id === parsedData.type)?.defaultValue;
    }, undefined);

    if (!(configuredSetting ?? defaultSetting)) return acc;

    const handler =
      parsedData.type in NOTIFICATION_HANDLERS
        ? NOTIFICATION_HANDLERS[parsedData.type as keyof typeof NOTIFICATION_HANDLERS]
        : undefined;

    let relatedId: string | undefined;
    if (handler) {
      relatedId = parsedData.relatedRecords?.find((r) => r.type === handler.relatedRecordType)?.id;
      if (!relatedId) return acc;
    }

    const dedupKey = handler && relatedId ? `${parsedData.type}:${relatedId}` : `${parsedData.type}:${parsedData.id}`;

    acc.set(dedupKey, { notification: parsedData, handler, relatedId });
    return acc;
  }, new Map<string, DedupedNotification>());

  const resolved = await Promise.all(
    Array.from(dedupedNotifications.values()).map(async ({ notification, handler, relatedId }) => {
      let prefetchedData: Record<string, unknown> | null = null;
      if (handler && relatedId) {
        try {
          prefetchedData = await handler.prefetchData(relatedId, notification, queryClient, session);
        } catch {
          prefetchedData = null;
        }
      }
      return { notification, handler, relatedId, prefetchedData };
    })
  );

  resolved.forEach(({ notification, handler, relatedId, prefetchedData }) => {
    if (
      handler?.predicate &&
      prefetchedData &&
      !handler.predicate({ notification, prefetchedData, currentUser: user })
    ) {
      return;
    }

    if (shouldUseOSNotification) {
      new Notification("Tellescope", {
        body: notification.message,
        icon: "/logo512.png",
        badge: "/logo192.png",
      });
    } else if (notification.message !== "test") {
      if (handler && relatedId && prefetchedData) {
        const Component = handler.component;
        toast.custom(
          (t) => (
            <div className="relative">
              <Component {...handler.mapProps(relatedId, notification, t, prefetchedData)} />
              <Button
                className="absolute top-1 right-1 p-1 h-6 w-6 min-h-4 min-w-4"
                size="sm-icon"
                variant="ghost"
                onClick={(e) => {
                  e.stopPropagation();
                  toast.dismiss(t);
                }}
              >
                <X />
              </Button>
            </div>
          ),
          {
            position: "top-right",
          }
        );
      } else {
        toast(notification.message, { position: "top-right" });
      }
    }
  });
};
